import posthog from 'posthog-js';
import {
  PostHogProvider as NativePostHogProvider,
  usePostHog,
} from 'posthog-js/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import '~/config/sentry';
import { countryNames, ValidLanguagesPerMarket } from '~/constants/markets';
import {
  PostHogContext,
  PostHogEvent,
} from '~/contexts/PostHogProvider/posthog.context';
import { logToSentry } from '~/helpers/loggers.helper';
import { getMarketBasedOnHostname } from '~/helpers/utils';
import { RootState } from '~/store';
import { Language } from '~/store/types/sharedTypes';

interface PostHogProviderProps {
  children: React.ReactNode;
}

const { VITE_CURRENT_VERSION, VITE_APP_NAME } = import.meta.env;

const setupMarketProperty = (): void => {
  try {
    const market = getMarketBasedOnHostname();

    if (!market) {
      logToSentry(
        new Error('Could not determine market from hostname'),
        'Market detection failed'
      );
      return;
    }

    // Set the market group for all events in this session
    posthog.group('market', market, {
      name: market,
      fullName: countryNames[market][Language.English],
      languages: ValidLanguagesPerMarket[market].join(', '),
    });

    // DEPRECATED, see https://qredab.atlassian.net/browse/OB-3841: use the market group instead of person properties
    // keep this for backward compatibility
    posthog.setPersonProperties({
      market,
    });
  } catch (error) {
    logToSentry(error, 'Error setting up market property');
  }
};

const {
  VITE_APP_PUBLIC_POSTHOG_KEY,
  VITE_APP_PUBLIC_POSTHOG_HOST,
  VITE_POSTHOG_TRACKING_REQUIRES_CONSENT,
  VITE_POSTHOG_EVENT_CAPTURING_ENABLED,
  VITE_POSTHOG_SESSION_RECORDING_ENABLED,
} = import.meta.env;

const shouldDisableEventCapturingByDefault =
  VITE_POSTHOG_TRACKING_REQUIRES_CONSENT === 'true' ||
  VITE_POSTHOG_EVENT_CAPTURING_ENABLED === 'false';

const shouldDisableSessionRecordingByDefault =
  VITE_POSTHOG_TRACKING_REQUIRES_CONSENT === 'true' ||
  VITE_POSTHOG_SESSION_RECORDING_ENABLED === 'false';

posthog.init(VITE_APP_PUBLIC_POSTHOG_KEY, {
  api_host: VITE_APP_PUBLIC_POSTHOG_HOST,
  capture_pageview: true,
  capture_pageleave: true,
  capture_heatmaps: true,
  enable_heatmaps: true,
  autocapture: true,
  mask_all_text: true,
  opt_out_capturing_by_default: shouldDisableEventCapturingByDefault,
  disable_session_recording: shouldDisableSessionRecordingByDefault,
  session_recording: {
    maskAllInputs: true,
    maskInputFn: (text, element) => {
      if (element?.dataset['record'] === 'true') {
        return text;
      }
      // Mask all Unicode letters and numbers, keep special characters and spaces
      return text.replace(/[\p{L}\p{N}]/gu, '*');
    },
    maskTextSelector: '*',
    maskTextFn: (text, element) => {
      try {
        const elementIsMaterialSymbol = element?.className?.includes?.(
          'material-symbols'
        );
        const elementIsMarkedForRecording =
          element?.dataset?.['record'] === 'true' ||
          element?.dataset?.['ph-unmask'] === 'true';

        if (elementIsMarkedForRecording || elementIsMaterialSymbol) {
          return text;
        }

        // Mask all Unicode letters and numbers, keep special characters and spaces
        return text.replace(/[\p{L}\p{N}]/gu, '*');
      } catch (error) {
        logToSentry(error, 'Error masking text in session recording');
        return '*';
      }
    },
  },
  before_send: (event) => {
    if (!event) {
      return null;
    }

    try {
      const market = getMarketBasedOnHostname();

      // Add app-level properties as event properties for additional filtering
      const persistentProperties = {
        appVersion: VITE_CURRENT_VERSION || 'unknown',
        appName: VITE_APP_NAME,
        market: market ?? 'unknown',
      };

      const newEvent = {
        ...event,
        properties: {
          ...persistentProperties,
          ...event.properties,
        },
      };

      return newEvent;
    } catch (error) {
      logToSentry(error, 'Error in PostHog before_send');
      return event;
    }
  },
  loaded: () => {
    try {
      setupMarketProperty();
    } catch (error) {
      logToSentry(error, 'Error in PostHog loaded callback');
    }
  },
});

const PostHogProvider = ({ children }: PostHogProviderProps) => {
  const [event, setEvent] = useState<PostHogEvent>(null as any);
  const memoizedValue = useMemo(() => ({ event, setEvent }), [event, setEvent]);
  const posthogClient = usePostHog();
  const { language, languageIsSet } = useSelector(
    (state: RootState) => state.intl
  );

  useEffect(() => {
    if (languageIsSet) {
      posthogClient.setPersonProperties({
        language,
      });
    }

    // DEPRECATED, see https://qredab.atlassian.net/browse/OB-3841: use the market group instead of person properties
    posthogClient.setPersonProperties({
      market: getMarketBasedOnHostname() ?? 'unknown',
    });
  }, [language, languageIsSet]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const queryParams = url.searchParams;
    const featureFlags = posthogClient.featureFlags.getFlags();
    let didOverride = false;
    featureFlags.forEach((flag) => {
      const featureFlagOverride = queryParams.get(flag);
      if (featureFlagOverride) {
        didOverride = true;
        posthogClient.featureFlags.overrideFeatureFlags({
          flags: {
            [flag]: featureFlagOverride,
          },
        });
      }
    });

    if (!didOverride) {
      posthog.featureFlags.overrideFeatureFlags(false);
    }
  }, []);

  return (
    <NativePostHogProvider client={posthog}>
      <PostHogContext.Provider value={memoizedValue}>
        {children}
      </PostHogContext.Provider>
    </NativePostHogProvider>
  );
};

export default PostHogProvider;
